import { COMMON_METATAG_TAG_TYPES } from "../constants";

////////////////////////////////////////////////////////////////////////////////
// mevn3 common-metatag.inits(02ce372)를 옮겼다. 원본과 다른 것:
// - 코드 만들기(공통 구간)는 백엔드 models/common/metatag.js 와 같다. 미리보기가 서버가 넣는 코드와 같아야 한다
// - HTML 텍스트 → 태그: 하이픈·콜론이 든 속성 이름과 따옴표 없는 값을 한 속성으로 읽는다
////////////////////////////////////////////////////////////////////////////////

// ---- 공통 구간 시작: 백엔드 src/models/common/metatag.js 의 공통 구간과 글자 그대로 같아야 한다
export const findMetatag_TagData_Link_Primary = ({ key }) => ["href"].includes(key);
export const findMetatag_TagData_Meta_Primary = ({ key }) => ["name", "property"].includes(key);

/** 닫는 태그가 없는 HTML void 요소. 그 밖의 종류는 본문이 없어도 </종류> 로 닫는다 */
export const COMMON_METATAG_VOID_TYPES = ["area", "base", "br", "col", "embed", "hr", "img", "input", "link", "meta", "source", "track", "wbr"];

/** 값 안의 ${이름} 을 등록된 변수 text 로 바꾼다. 이름을 글자 그대로 비교하고, 등록되지 않은 이름은 그대로 둔다. 바꿔 넣은 text 안의 ${} 는 다시 바꾸지 않는다 */
export function interpolateCommonMetatag_Value(value, variables_object = {}) {
    const object = variables_object || {};
    return String(value).replace(/\$\{([^{}]*)\}/g, (match, name) => (Object.prototype.hasOwnProperty.call(object, name) ? String(object[name] ?? "") : match));
}

const escapeCommonMetatag_Text = (value) => String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const escapeCommonMetatag_Attr = (value) => escapeCommonMetatag_Text(value).replace(/"/g, "&quot;");

/** 출력하는 태그인지. 본문(slot) 없는 title 은 출력하지 않는다 */
export const isCommonMetatag_Printable = ({ type, data }) => type != "title" || data.some(({ key }) => key == "slot");

/**
 * 서버가 index.html 에 넣는 태그의 data-vmid. 사용자 화면(AppMain.vue)이 vue-meta 에 같은 vmid 로 넘긴다.
 * meta 는 name → property, link 는 href 의 값. 그 값이 없는 meta·link 는 <종류>:<그런 태그의 순번>.
 * script 는 src 가 있으면 script:<src>, 없으면 script:inline:<본문 있는 script 의 순번>. 그 밖의 종류는 <종류>:<순번>.
 * 순번은 출력하는 태그 목록 안에서 0부터 센다. props·body 는 변수 치환을 마친 값이다
 */
function toCommonMetatag_Vmid(type, props, body, counts) {
    const valueOf = (name) => props.find(({ key }) => key == name)?.value;
    switch (type) {
        case "meta":
        case "link": {
            const primary = type == "meta" ? findMetatag_TagData_Meta_Primary : findMetatag_TagData_Link_Primary;
            const key = ["name", "property", "href"].find((key) => primary({ key }) && valueOf(key));
            if (key) return `${key}:${valueOf(key)}`;
            counts[type] = (counts[type] ?? -1) + 1;
            return `${type}:${counts[type]}`;
        }
        case "script": {
            const index = body.trim() ? (counts.script = (counts.script ?? -1) + 1) : null;
            const src = valueOf("src");
            if (src) return `script:${src}`;
            return index === null ? null : `script:inline:${index}`;
        }
        default: {
            counts[type] = (counts[type] ?? -1) + 1;
            return `${type}:${counts[type]}`;
        }
    }
}

/**
 * 태그 목록을 <head> 에 넣을 HTML 로 만든다.
 * this.variables_object 로 ${이름} 을 바꾸고, this.vmid 가 true 이면 data-vmid 를 붙인다
 */
export function initCommonMetatag_Code(tags = []) {
    const counts = Object.create(null);
    return [
        ...(tags || []).filter(isCommonMetatag_Printable).map(({ type, data }) => {
            let data_slots = data.filter(({ key }) => key == "slot");
            let data_props = data.filter(({ key }) => key != "slot");

            let needsWrap = 1 < data_slots.length || 0 < data_props.length;

            let props = data_props.map(({ key, value }) => ({ key, value: interpolateCommonMetatag_Value(value || "", this?.variables_object || {}) }));
            let slot = data_slots.map(({ value }) => interpolateCommonMetatag_Value(value || "", this?.variables_object || {}));
            slot = slot.join("\n");

            if (this?.vmid) {
                let vmid = toCommonMetatag_Vmid(type, props, slot, counts);
                if (vmid) props = [...props.filter(({ key }) => key != "data-vmid"), { key: "data-vmid", value: vmid }];
            }

            let tag = `<${type}`;

            if (0 < props.length) {
                let prop = props.map(({ key, value }) => (value === "" ? key : `${key}="${escapeCommonMetatag_Attr(value)}"`));
                prop = prop.join(" ");
                tag += ` ${prop}`;
            }

            if (0 < data_slots.length) {
                tag += ">";

                if (needsWrap) tag += "\n";

                tag += type == "title" ? escapeCommonMetatag_Text(slot) : slot;

                if (needsWrap) tag += "\n";

                tag += `</${type}>`;
            } else if (COMMON_METATAG_VOID_TYPES.includes(type)) {
                tag += " />";
            } else {
                tag += `></${type}>`;
            }

            return tag;
        }),
    ].join("\n");
}
// ---- 공통 구간 끝

/** 프론트만: AppMain.vue 가 vue-meta 에 넘길 vmid. tags 는 출력하는 태그(isCommonMetatag_Printable)이고 변수 치환을 마친 것. 서버 data-vmid 와 같은 규칙이다 */
export function initCommonMetatag_Vmids(tags = []) {
    const counts = Object.create(null);
    return (tags || []).map(({ type, data }) => {
        let props = data.filter(({ key }) => key != "slot").map(({ key, value }) => ({ key, value: value || "" }));
        let slot = data.filter(({ key }) => key == "slot").map(({ value }) => value || "");
        return toCommonMetatag_Vmid(type, props, slot.join("\n"), counts);
    });
}

export function initCommonMetatag(form = {}) {
    let tags = [...(form?.tags || [{ type: COMMON_METATAG_TAG_TYPES.title.value }, { type: COMMON_METATAG_TAG_TYPES.meta.value }])].map(initCommonMetatag_Tag);

    return {
        ...(form || {}),

        path: form?.path || null,

        tags,

        code: initCommonMetatag_Code.bind(this)(tags),

        uses: form?.uses ?? true,
    };
}

export function initCommonMetatag_Tag(form = {}) {
    return {
        ...(form || {}),

        type: form?.type || null,

        data: [...(form?.data || [{}])].map(initCommonMetatag_Tag_Data),

        uses: form?.uses ?? true,

        tempId: form?.tempId ?? Math.random().toString().slice(2, 10),
    };
}

export function initCommonMetatag_Tag_Data(form = {}) {
    return {
        ...(form || {}),

        key: form?.key || null,
        value: form?.value || null,

        tempId: form?.tempId ?? Math.random().toString().slice(2, 10),
    };
}

// initCommonMetatag_Code 가 escape 한 속성값·title 본문을 되돌린다. 안 되돌리면 텍스트 모드를 오갈 때마다 &amp; 가 쌓인다
const unescapeCommonMetatag_Text = (value) => String(value).replace(/&quot;/g, '"').replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");

export function initCommonMetatag_CodeToTags(code) {
    let result = [];

    const tagRegex = /<([\w]+)([^>]*)>([\s\S]*?)<\/\1>|<([\w]+)([^>]*?)\/?>(?!\s*<\/)/g;

    let match;
    while ((match = tagRegex.exec(code)) !== null) {
        const tagName = match[1] || match[4];
        const attributes = match[2] || match[5] || "";
        const innerText = match[3] || "";

        const tagObject = {
            type: tagName,
            data: [],
        };

        // 속성 이름은 공백·따옴표·< > / = 가 아닌 글자 (http-equiv, data-x, xml:lang). 값은 따옴표로 감싼 것 또는 따옴표 없는 것
        const attrRegex = /([^\s"'<>\/=]+)(?:=(?:(["'])(.*?)\2|([^\s"'=<>`]+)))?/g;
        let attrMatch;
        while ((attrMatch = attrRegex.exec(attributes)) !== null) {
            const attrName = attrMatch[1];
            const attrValue = attrMatch[3] ?? attrMatch[4];

            tagObject.data.push({
                key: attrName,
                value: unescapeCommonMetatag_Text(attrValue ?? ""),
            });
        }

        if (innerText.trim()) {
            tagObject.data.push({
                key: "slot",
                value: tagName == "title" ? unescapeCommonMetatag_Text(innerText.trim()) : innerText.trim(),
            });
        }

        result.push(tagObject);
    }

    return result;
}
